// Shared types + display helpers for the Fleet module.
//
// The vehicle vocabulary and the active rule live in ./vehicleActive, which
// imports nothing so the test runner can load it and compare it against the SQL
// in db/migrations/0126. Everything is re-exported from here, so no caller has to
// know about the split.
import { formatDate, initials } from './interviews'
import {
  type ActiveInput,
  type LocationBucket,
  type StatusOption,
  type StatusTone,
  HOLD_REASON_OPTIONS,
  LEFT_THE_FLEET,
  LIFECYCLE_STATUS_OPTIONS,
  LOCATION_BUCKETS,
  LOCATION_OPERATING,
  LOCATION_OPTIONS,
  fleetStatusLabel,
  fleetStatusTone,
  holdReasonChips,
  holdReasonLabel,
  holdReasonTone,
  humanise,
  inactiveReasons,
  isActive,
  locationBucket,
  locationLabel,
  locationTone,
} from './vehicleActive'

export { formatDate, initials }

export type { ActiveInput, LocationBucket, StatusOption, StatusTone }

export {
  HOLD_REASON_OPTIONS,
  LEFT_THE_FLEET,
  LIFECYCLE_STATUS_OPTIONS,
  LOCATION_BUCKETS,
  LOCATION_OPERATING,
  LOCATION_OPTIONS,
  fleetStatusLabel,
  fleetStatusTone,
  holdReasonChips,
  holdReasonLabel,
  holdReasonTone,
  humanise,
  inactiveReasons,
  isActive,
  locationBucket,
  locationLabel,
  locationTone,
}

export type FleetRow = {
  vehicle_id: string
  registration_number: string | null
  make: string | null
  model: string | null
  year: number | null
  colour: string | null
  lifecycle_status: string
  in_general_fleet: boolean | null
  hold_reasons: string[] | null
  /** LEGACY. The generated single-value mirror kept for Retool. Never write it. */
  hold_reason: string | null
  location: string | null
  operational: boolean | null
  /** Computed by vehicle_is_active_v1 in the database. Never a stored column. */
  is_active: boolean | null
  /** Every condition that is failing, in words. Empty when the car is active. */
  inactive_reasons: string[] | null
  // From v_vehicle_uptime, evidenced by shifts. NULL, never zero, for a vehicle
  // with no shift on record: zero days offline on a car nobody has ever driven
  // would be a lie the screen tells itself.
  /** Days since the last shift on or before today. */
  days_offline: number | null
  /** Distinct non-cancelled shift dates over days since the first shift. */
  availability_pct: number | null
  /** Every day since the first shift on which the car did not work, added up. */
  offline_days_total: number | null
  active_days: number | null
  platform_days: number | null
  first_shift_date: string | null
  last_shift_date: string | null
  telematics_status: string | null
  licensing_status: string | null
  compliance_status: string | null
  next_service_date: string | null
  license_expiry: string | null
  repairs_due: string | null
  financed: boolean | null
  financier: string | null
  finance_months_left: number | null
  instalment_zar: number | null
  owner_id: string | null
  owner_name: string | null
  owner_phone: string | null
  owner_email: string | null
  current_driver: string | null
  open_incident_status: string | null
  last_incident_outcome: string | null
  last_incident_type: string | null
  last_incident_at: string | null
}

/** The row as the database currently reads it, used to decide what a save changes. */
export function rowActiveInput(row: FleetRow): ActiveInput {
  return {
    lifecycle_status: row.lifecycle_status,
    in_general_fleet: row.in_general_fleet,
    hold_reasons: row.hold_reasons,
    operational: row.operational,
    location: row.location,
  }
}

export const INCIDENT_TYPE_OPTIONS: StatusOption[] = [
  { code: 'accident', label: 'Accident' },
  { code: 'mechanical', label: 'Mechanical' },
  { code: 'other', label: 'Other' },
]

export const INCIDENT_STATUS_OPTIONS: StatusOption[] = [
  { code: 'open', label: 'Open' },
  { code: 'investigating', label: 'Investigating' },
  { code: 'resolved', label: 'Resolved' },
  { code: 'escalated', label: 'Escalated' },
  { code: 'closed', label: 'Closed' },
]
